import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ProfessionalLayout } from '@/modules/professional/layouts/ProfessionalLayout';
import { PaymentValidator } from '@/modules/professional/components/PaymentValidator';
import { TransactionalChat } from '@/modules/professional/components/chat/TransactionalChat';
import { PaymentQueueRepository } from '@/core/application/financial/PaymentQueueRepository';
import type { Comprobante, Decision } from '@/core/application/financial/PaymentQueueRepository';
import { ArrowLeft, AlertTriangle, Flag } from 'lucide-react';

/**
 * RevisionCitaScreen — La Pantalla Híbrida.
 * 
 * Compone dos grandes bloques de forma aislada:
 * 1. PaymentValidator: Muestra los datos financieros y permite aprobar/rechazar.
 * 2. TransactionalChat: Permite la resolución de problemas con el paciente.
 */
export function RevisionCitaScreen() {
  const { citaId } = useParams<{ citaId: string }>();
  const navigate = useNavigate();
  const [comprobante, setComprobante] = useState<Comprobante | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const cargarDatos = useCallback(async () => {
    if (!citaId) return;
    setLoading(true);
    const data = await PaymentQueueRepository.fetchComprobanteById(citaId);
    if (data) {
      setComprobante(data);
    } else {
      setError('No se encontró el comprobante o no tienes permisos.');
    }
    setLoading(false);
  }, [citaId]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const handleDecision = async (id: string, decision: Decision, nota?: string) => {
    const result = await PaymentQueueRepository.procesarDecision(id, decision, nota);
    if (result.success) {
      // Recargar para ver el nuevo estado
      await cargarDatos();
    } else {
      globalThis.alert(result.message ?? 'Error procesando la decisión');
    }
  };

  const handleReportar = async () => {
    if (!comprobante) return;
    const motivo = globalThis.prompt('¿Cuál es el motivo de la disputa? Esta acción congelará la cita y se reportará.');
    if (!motivo) return;

    const result = await PaymentQueueRepository.reportarDisputa(comprobante.cita_id, motivo);
    if (result.success) {
      globalThis.alert('La cita ha sido marcada en disputa y congelada exitosamente.');
      navigate('/pro/comprobantes');
    } else {
      globalThis.alert(result.message ?? 'Error al reportar la disputa');
    }
  };

  if (loading) {
    return (
      <ProfessionalLayout>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: '16px', backgroundColor: '#F8FAFC' }}>
          <div style={{ width: 40, height: 40, border: `4px solid #0052CC40`, borderTopColor: '#0052CC', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
          <span style={{ fontSize: '15px', color: '#64748B' }}>Cargando detalles...</span>
        </div>
        <style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
      </ProfessionalLayout>
    );
  }

  if (error || !comprobante) {
    return (
      <ProfessionalLayout>
        <div style={{ flex: 1, backgroundColor: '#F8FAFC', padding: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <AlertTriangle size={48} color="#EF4444" style={{ marginBottom: '16px' }} />
          <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#0A2540', marginBottom: '8px' }}>Error</h2>
          <p style={{ color: '#64748B' }}>{error}</p>
          <button 
            onClick={() => navigate('/pro/comprobantes')}
            style={{ marginTop: '24px', padding: '10px 20px', backgroundColor: '#0052CC', color: '#FFF', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: 600 }}
          >
            Volver a la cola
          </button>
        </div>
      </ProfessionalLayout>
    );
  }

  return (
    <ProfessionalLayout>
      <div style={{ flex: 1, backgroundColor: '#F8FAFC', display: 'flex', flexDirection: 'column', height: '100%' }}>
        
        {/* HEADER */}
        <header style={{
          display: 'flex', alignItems: 'center', backgroundColor: '#FFFFFF', padding: '14px 16px',
          borderBottom: '1px solid #E2E8F0'
        }}>
          <button 
            onClick={() => navigate('/pro/comprobantes')}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', cursor: 'pointer', color: '#0052CC', padding: '4px 8px', marginLeft: '-8px' }}
          >
            <ArrowLeft size={20} />
            <span style={{ fontSize: '15px', fontWeight: 600 }}>Volver</span>
          </button>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <h1 style={{ fontSize: '16px', fontWeight: 700, color: '#0A2540', margin: 0 }}>Revisión de Cita</h1>
          </div>
          <div style={{ width: 'auto', minWidth: '70px', display: 'flex', justifyContent: 'flex-end' }}>
            <button 
              onClick={handleReportar}
              title="Reportar problema ético o fraude"
              style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', padding: '4px 8px' }}
            >
              <Flag size={18} />
              <span style={{ fontSize: '13px', fontWeight: 600 }}>Reportar</span>
            </button>
          </div>
        </header>

        {/* CONTENIDO SCROLLABLE */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          
          {/* Bloque 1: Validador de Pago */}
          <div>
            <PaymentValidator 
              comprobantes={[comprobante]} 
              onDecision={handleDecision} 
              forceExpanded={true}
            />
          </div>

          {/* Bloque 2: Chat Transaccional */}
          <div style={{ flex: 1, minHeight: '400px' }}>
            {comprobante && <TransactionalChat citaId={comprobante.cita_id} profesionalUuid={comprobante.profesional_uuid} pacienteUuid={comprobante.paciente_uuid} />}
          </div>

        </div>
      </div>
    </ProfessionalLayout>
  );
}
